import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AuthService, BreadcrumbService, ConfirmDialogService, ProductNameService, StateService } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfigService } from 'src/app/services/config.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  GetReleaseRequest, GetReleaseResponse,
  ListReleasesRequest, ListReleasesResponse,
  ListRenderedOutputsRequest, ListRenderedOutputsResponse, LoadProductUsageFromArtifactRequest, ProductName, Release, RenderedOutputStatus,
  RenderedOutputSummary, SituViewWebApiClient, SetRenderedOutputStatusRequest, SetRenderedOutputStatusResponse,
  SetRenderedOutputProductionRequest, SetRenderedOutputProductionResponse
} from 'situview-webapi-client';
import { ListSituConnectSummariesRequest, ListSituConnectSummariesResponse, RecipeStatus, SituAnalyticsWebApiClient, SituConnectSummary } from 'situanalytics-webapi-client';
import { MatTableDataSource } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { ViewControls } from 'src/app/shared/models/view-controls';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';

@Component({
  selector: 'situ-browse',
  templateUrl: './situ-browse.component.html',
  styleUrls: ['./situ-browse.component.scss']
})
export class SituBrowseComponent extends SituBaseComponent {
  @Input() allowRenderCancelation: boolean = true;
  @Input() allowOpenSituViewOrFacade: boolean = true;
  /** Pre-filter to one style template (e.g. a scratch template's history). Route query params
   *  are read in the constructor, so this input — bound before ngOnInit — takes precedence. */
  @Input() incomingStyleConfigTemplateId: string | undefined;
  /** Hosted in a dialog rather than on its own route: don't inherit the host page's query params
   *  and don't write filters back to its URL. */
  @Input() embedded: boolean = false;

  @Output() selectedRenderedOutput: EventEmitter<RenderedOutputSummary> = new EventEmitter();

  private readonly DefaultPageSize: number = 12;

  /* view mode */
  isSituConnectMode: boolean = false;

  /* view data */
  viewControls = new ViewControls(this.DefaultPageSize, 0, 0, [this.DefaultPageSize, 25, 50, 100]);
  dataSource = new MatTableDataSource<RenderedOutputSummary>();
  situConnectDataSource: SituConnectSummary[] = [];

  /* search params */
  private readonly ProductParamName: string = "product";
  readonly FilterParamName: string = "filter";
  readonly StyleParamName: string = "style";
  readonly ConfigParamName: string = "config";
  readonly HomeParamName: string = "home";
  readonly ReleaseParamName: string = "release";
  readonly StyleNameParamName: string = "styleName";
  readonly MasterFileParamName: string = "masterFile";
  readonly HomeNameParamName: string = "homeName";
  private readonly LiveOnlyParamName: string = "liveOnly";
  private readonly PageSizeParamName: string = "pageSize";
  isProductionOnly: boolean = false;
  filter: string = "";
  product: ProductName | undefined;
  style: string | undefined
  config: string | undefined
  home: string | undefined
  release: string | undefined;
  styleTemplateName: string | undefined;
  masterFile: string | undefined;
  homeName: string | undefined;

  productNames = ProductName;

  scanningArtifactId: string | undefined;

  /* advanced filters */
  showAdvancedFilters: boolean = false;
  releases: Release[] = [];
  selectedRelease: Release | null = null;
  releaseSearch: string = "";
  homeSearch: string = "";
  styleTemplateSearch: string = "";
  masterFileSearch: string = "";

  /* product type options for dropdown */
  productTypeOptions = [
    { value: undefined, label: 'All Products' },
    { value: ProductName.Panorama, label: 'situVIEW' },
    { value: ProductName.RayTracedDraft, label: 'situVIEW Draft' },
    { value: ProductName.RayTraced, label: 'situVIEW HD' },
    { value: ProductName.RayTracedPlus, label: 'situVIEW HD+' },
    { value: ProductName.Facade, label: 'situFACADE' },
    { value: ProductName.Plan, label: 'situPLAN' },
    { value: ProductName.Render, label: 'situRENDER' },
    { value: ProductName.Unreal, label: 'situHOME' }
  ];

  /* loading */
  private _loading = false;
  get loading(): boolean { return this._loading; }
  set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private configService: ConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private situViewClient: SituViewWebApiClient,
    private productNameService: ProductNameService,
    private dialogService: ConfirmDialogService,
    private situAnalyticsClient: SituAnalyticsWebApiClient) { 
      super(authService, stateService);

      // Check if we're on the situ-connect browse route
      if (this.router.url.includes('/situ-connect/browse')) {
        this.isSituConnectMode = true;
      }

      if (this.route.snapshot.params[this.ProductParamName]) {
        let numberValue = parseInt(this.route.snapshot.params[this.ProductParamName]);
        if (!isNaN(numberValue)) {
          this.product = numberValue;
        }
      }
      if (this.route.snapshot.queryParams[this.FilterParamName]) {
        this.filter = this.route.snapshot.queryParams[this.FilterParamName];
      }
      if (this.route.snapshot.queryParams[this.LiveOnlyParamName]) {
        this.isProductionOnly = this.route.snapshot.queryParams[this.LiveOnlyParamName] === "true";
      }
      if (this.route.snapshot.queryParams[this.PageSizeParamName]) {
        this.viewControls.pageSize = this.route.snapshot.queryParams[this.PageSizeParamName];
      }
      if (this.route.snapshot.queryParams[this.StyleParamName]) {
        this.style = this.route.snapshot.queryParams[this.StyleParamName];
      }
      if (this.route.snapshot.queryParams[this.ConfigParamName]) {
        this.config = this.route.snapshot.queryParams[this.ConfigParamName];
      }
      if (this.route.snapshot.queryParams[this.HomeParamName]) {
        this.home = this.route.snapshot.queryParams[this.HomeParamName];
      }
      if (this.route.snapshot.queryParams[this.ReleaseParamName]) {
        this.release = this.route.snapshot.queryParams[this.ReleaseParamName];
      }
      if (this.route.snapshot.queryParams[this.StyleNameParamName]) {
        this.styleTemplateName = this.route.snapshot.queryParams[this.StyleNameParamName];
      }
      if (this.route.snapshot.queryParams[this.MasterFileParamName]) {
        this.masterFile = this.route.snapshot.queryParams[this.MasterFileParamName];
      }
      if (this.route.snapshot.queryParams[this.HomeNameParamName]) {
        this.homeName = this.route.snapshot.queryParams[this.HomeNameParamName];
      }
      if (this.release || this.styleTemplateName || this.masterFile || this.homeName) {
        this.showAdvancedFilters = true;
      }
  }

  ngOnInit(): void {
    if (this.embedded) {
      // The constructor already read the host page's query params — discard them, since a dialog
      // should start from a clean slate rather than inherit (or leak) the page's filter state.
      this.filter = "";
      this.isProductionOnly = false;
      this.style = undefined;
      this.config = undefined;
      this.home = undefined;
      this.release = undefined;
      this.selectedRelease = null;
      this.styleTemplateName = undefined;
      this.masterFile = undefined;
      this.homeName = undefined;
      this.releaseSearch = '';
      this.homeSearch = '';
      this.styleTemplateSearch = '';
      this.masterFileSearch = '';
      this.showAdvancedFilters = false;
    }

    if (this.incomingStyleConfigTemplateId) {
      this.style = this.incomingStyleConfigTemplateId;
    }

    super.ngOnInit();
  }

  protected onSelectedCompanyChange(): void {
    if (this.isSituConnectMode) {
      this.loadSituConnectRenders();
    } else {
      this.loadReleases();
      if (this.release) {
        this.fetchSelectedRelease(this.release);
      }
      this.loadRenderedOutputs();
    }
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  loadReleases(): void {
    if (!this.selectedCompany?.id) {
      this.releases = [];
      return;
    }
    const request = new ListReleasesRequest({
      companyId: this.selectedCompany.id,
      pageNumber: 1,
      pageSize: 200,
      includeStats: false
    });
    this.situViewClient.listReleases(request)
      .then((response: ListReleasesResponse) => {
        if (response?.success && response.items) {
          this.releases = response.items
            .slice()
            .sort((a, b) => {
              const aT = a.releaseDate instanceof Date ? a.releaseDate.getTime() : 0;
              const bT = b.releaseDate instanceof Date ? b.releaseDate.getTime() : 0;
              return bT - aT;
            });
        }
      })
      .catch(() => {
        // releases are an enhancement, swallow failures so the page still loads
      });
  }

  private fetchSelectedRelease(releaseId: string): void {
    if (!this.selectedCompany?.id || !releaseId) {
      this.selectedRelease = null;
      return;
    }
    const request = new GetReleaseRequest({ companyId: this.selectedCompany.id, releaseId });
    this.situViewClient.getRelease(request)
      .then((response: GetReleaseResponse) => {
        if (response?.success && response.release) {
          this.selectedRelease = response.release;
          if (response.release.name && !this.releaseSearch) {
            this.releaseSearch = response.release.name;
          }
          if (this.homeName && !this.homeSearch) {
            this.homeSearch = this.homeName;
          }
          if (this.styleTemplateName && !this.styleTemplateSearch) {
            this.styleTemplateSearch = this.styleTemplateName;
          }
          if (this.masterFile && !this.masterFileSearch) {
            this.masterFileSearch = this.masterFile;
          }
        } else {
          this.selectedRelease = null;
        }
      })
      .catch(() => {
        this.selectedRelease = null;
      });
  }

  filteredReleases(): Release[] {
    // mat-autocomplete with `[value]="releaseObject"` briefly writes the Release
    // object back into the ngModel right after a selection — coerce to string
    // so the filter doesn't crash.
    const raw = typeof this.releaseSearch === 'string' ? this.releaseSearch : '';
    const term = raw.toLowerCase().trim();
    const selectedName = this.selectedRelease?.name?.toLowerCase();
    // If the input still shows the currently-selected release, treat that as
    // "no filter" so the user can pick a different one without clearing first.
    if (!term || (selectedName && term === selectedName)) {
      return this.releases.slice(0, 50);
    }
    return this.releases.filter(r => r.name?.toLowerCase().includes(term)).slice(0, 50);
  }

  displayReleaseName = (value: Release | string | null): string => {
    if (!value) return '';
    // When the ngModel already holds the display string (typing or bootstrap
    // from URL), pass it through. When mat-autocomplete passes the option's
    // Release value after selection, extract the name.
    return typeof value === 'string' ? value : (value.name ?? '');
  };

  onReleaseSelected(release: Release): void {
    if (!release?.id) return;
    this.release = release.id;
    this.selectedRelease = release;
    // Material wrote the Release object into the ngModel — overwrite with the
    // display string so subsequent filter calls operate on a string.
    this.releaseSearch = release.name ?? '';
    // Picking a new release invalidates the secondary filters that were scoped
    // to the previous release.
    this.homeName = undefined;
    this.styleTemplateName = undefined;
    this.masterFile = undefined;
    this.homeSearch = '';
    this.styleTemplateSearch = '';
    this.masterFileSearch = '';
    this.viewControls.pageIndex = 0;

    // We have the full Release with items in hand, but fetch again to make sure
    // we have the freshest state when the user lingers on the page.
    this.fetchSelectedRelease(release.id);
    this.updateParams();
    this.loadRenderedOutputs();
  }

  clearRelease(): void {
    this.release = undefined;
    this.selectedRelease = null;
    this.releaseSearch = '';
    this.homeName = undefined;
    this.styleTemplateName = undefined;
    this.masterFile = undefined;
    this.homeSearch = '';
    this.styleTemplateSearch = '';
    this.masterFileSearch = '';
    this.updateParams();
    this.loadRenderedOutputs();
  }

  releaseHomes(): string[] {
    if (!this.selectedRelease?.releaseItems) return [];
    const seen = new Set<string>();
    for (const item of this.selectedRelease.releaseItems) {
      const home = item.home?.trim();
      if (home) seen.add(home);
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }

  filteredReleaseHomes(): string[] {
    const term = (this.homeSearch ?? '').toLowerCase().trim();
    const all = this.releaseHomes();
    if (!term) return all;
    return all.filter(h => h.toLowerCase().includes(term));
  }

  onReleaseHomeSelected(name: string): void {
    if (!name) return;
    this.homeName = name;
    this.viewControls.pageIndex = 0;
    this.updateParams();
    this.loadRenderedOutputs();
  }

  releaseStyleTemplates(): string[] {
    if (!this.selectedRelease?.releaseItems) return [];
    const seen = new Set<string>();
    for (const item of this.selectedRelease.releaseItems) {
      const name = item.styleTemplate?.trim();
      if (name) seen.add(name);
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }

  filteredReleaseStyleTemplates(): string[] {
    const term = (this.styleTemplateSearch ?? '').toLowerCase().trim();
    const all = this.releaseStyleTemplates();
    if (!term) return all;
    return all.filter(s => s.toLowerCase().includes(term));
  }

  onStyleTemplateSelected(name: string): void {
    if (!name) return;
    this.styleTemplateName = name;
    this.viewControls.pageIndex = 0;
    this.updateParams();
    this.loadRenderedOutputs();
  }

  releaseMasterFiles(): string[] {
    if (!this.selectedRelease?.releaseItems) return [];
    const seen = new Set<string>();
    for (const item of this.selectedRelease.releaseItems) {
      const name = item.masterFile?.trim();
      if (name) seen.add(name);
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }

  filteredReleaseMasterFiles(): string[] {
    const term = (this.masterFileSearch ?? '').toLowerCase().trim();
    const all = this.releaseMasterFiles();
    if (!term) return all;
    return all.filter(s => s.toLowerCase().includes(term));
  }

  onMasterFileSelected(name: string): void {
    if (!name) return;
    this.masterFile = name;
    this.viewControls.pageIndex = 0;
    this.updateParams();
    this.loadRenderedOutputs();
  }

  selectedReleaseName(): string {
    if (this.selectedRelease?.name) return this.selectedRelease.name;
    const match = this.releases.find(r => r.id === this.release);
    return match?.name ?? '';
  }

  selectedHomeName(): string {
    return this.homeName ?? '';
  }

  getProductColour(productName: ProductName) {
    return this.productNameService.getProductNameConfiguration(productName)?.productColor;
  }

  clearFilter(filter: string) {
    switch(filter) {
      case this.FilterParamName:
        this.filter = "";
        break;
      case this.StyleParamName:
        this.style = undefined;
        break;
      case this.ConfigParamName:
        this.config = undefined;
        break;
      case this.HomeParamName:
        this.home = undefined;
        break;
      case this.HomeNameParamName:
        this.homeName = undefined;
        this.homeSearch = '';
        break;
      case this.StyleNameParamName:
        this.styleTemplateName = undefined;
        this.styleTemplateSearch = '';
        break;
      case this.MasterFileParamName:
        this.masterFile = undefined;
        this.masterFileSearch = '';
        break;
    }
    this.updateParams();
    this.loadRenderedOutputs();
  }

  toggleIsProductionOnly() {
    this.isProductionOnly = !this.isProductionOnly;
    this.updateParams();
    this.loadRenderedOutputs();
  }

  onProductTypeChange(productName: ProductName | undefined) {
    this.product = productName;
    this.viewControls.pageIndex = 0;

    // Navigate to the correct route with product as route param
    const basePath = this.router.url.includes('/situ-facade/browse') ? '/situ-facade/browse' : '/situ-view/browse';
    const routePath = productName !== undefined ? `${basePath}/${productName}` : basePath;

    // Preserve existing query params
    this.router.navigate([routePath], {
      queryParams: {
        filter: this.filter || null,
        liveOnly: this.isProductionOnly ? 'true' : null,
        pageSize: this.viewControls.pageSize !== this.DefaultPageSize ? this.viewControls.pageSize : null,
        style: this.style || null,
        config: this.config || null
      },
      queryParamsHandling: 'merge'
    });

    // Reload the data since same component won't re-initialize
    this.loadRenderedOutputs();
  }

  getProductLabel(productName: ProductName | undefined): string {
    const option = this.productTypeOptions.find(opt => opt.value === productName);
    return option?.label ?? 'All Products';
  }

  setAsProduction(renderedOutput: RenderedOutputSummary, event: Event) {
    event.stopPropagation();

    if (!renderedOutput || renderedOutput.isProduction) {
      return;
    }

    const options: ConfirmDialogData = {
      title: 'Set to Live confirmation',
      message: `Are you sure you want to set this render as Live? This will make it the production version for "${renderedOutput.homeConfig?.home}".`,
      cancelText: 'Cancel',
      confirmText: 'Set to Live',
      actionIcon: 'verified',
      actionColour: 'primary'
    };

    this.dialogService.open(options);
    this._subscriptions.push(this.dialogService.confirmed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.loading = true;
          let request = new SetRenderedOutputProductionRequest();
          request.companyId = this.selectedCompany?.id;
          request.renderedOutputId = renderedOutput.id;

          this.situViewClient.setRenderedOutputProduction(request)
            .then((response: SetRenderedOutputProductionResponse) => {
              if (response.success) {
                this.showMessage("This has been set to Live!");
                // Update the local data to reflect the change
                renderedOutput.isProduction = true;
              } else {
                this.showMessage("Sorry! There was an issue setting this to Live. Please try again.");
              }
            })
            .catch((x: any) => {
              this.showMessage("Sorry! There was an issue at our end. Please try again.");
            })
            .finally(() => {
              this.loading = false;
            });
        }
      })
    );
  }

  toggleSituConnectMode() {
    this.isSituConnectMode = !this.isSituConnectMode;
    if (this.isSituConnectMode) {
      this.loadSituConnectRenders();
    } else {
      this.loadRenderedOutputs();
    }
  }

  cancelRenderedOutput(renderedOutput: RenderedOutputSummary) {
    if (!renderedOutput) {
      return;
    }
    this.loading = true;

    const options: ConfirmDialogData = {
      title: 'Cancel rendered output confirmation',
      message: 'Are you sure? Cancelling a rendered output can have very negative consequences.',
      cancelText: 'No',
      confirmText: 'Yes',
      actionIcon: 'delete',
      actionColour: 'warn'
    };
    this.dialogService.open(options);
    this._subscriptions.push(this.dialogService.confirmed()
      .subscribe(confirmed => {
        if (confirmed) {
          let request = new SetRenderedOutputStatusRequest();
          request.renderedOutputId = renderedOutput.artifactId;
          request.status = RenderedOutputStatus.Cancelled;
  
          this.situViewClient.setRenderedOutputStatus(request)
            .then((response: SetRenderedOutputStatusResponse) => {
              if (response.success) {
                this.dataSource.data = this.dataSource.data.filter(x => x.artifactId !== renderedOutput.artifactId);
              } else {
                this.showMessage("Sorry! There was an issue at our end cancelling this rendered output. Please try again.");
              }
            })
            .catch((x: any) => {
              this.showMessage("Sorry! There was an issue at our end cancelling this rendered output. Please try again.");
            })
            .finally(() => {
              this.loading = false;
            });
        } else {
          this.loading = false;
        }
      }));
  }

  canScanForProductUsage(item: RenderedOutputSummary): boolean {
    if (!item || item.status !== RenderedOutputStatus.Complete) return false;
    return item.productName === ProductName.Panorama
      || item.productName === ProductName.RayTracedDraft
      || item.productName === ProductName.RayTraced
      || item.productName === ProductName.RayTracedPlus;
  }

  scanForProductUsage(item: RenderedOutputSummary, event: Event) {
    event.stopPropagation();
    if (!item?.artifactId || this.scanningArtifactId) return;
    const artifactId = item.artifactId;
    this.scanningArtifactId = artifactId;

    const request = new LoadProductUsageFromArtifactRequest({ renderedOutputId: artifactId });
    this.situViewClient.loadProductUsageFromArtifact(request)
      .then(response => {
        if (response?.success) {
          const snack = this.snackBar.open('Product usage scanned', 'View', { duration: 5000 });
          snack.onAction().subscribe(() => {
            this.router.navigate(['/products/discover'], { queryParams: { renderedOutputId: artifactId } });
          });
        } else {
          const message = response?.errorMessages?.join(', ') || 'Failed to scan for product usage';
          this.showMessage(message);
        }
      })
      .catch(() => {
        this.showMessage('Sorry! There was an issue scanning for product usage. Please try again.');
      })
      .finally(() => {
        this.scanningArtifactId = undefined;
      });
  }

  onPageChange(event: PageEvent) {
    this.viewControls.pageSize = event.pageSize;
    this.viewControls.pageIndex = event.pageIndex;

    if (this.isSituConnectMode) {
      this.loadSituConnectRenders();
    } else {
      this.loadRenderedOutputs();
    }
  }

  onRenderSelected(renderedOutput: RenderedOutputSummary, newWindow: boolean = false) {
    if (this.allowOpenSituViewOrFacade) {
      let url: string;
    
      switch (renderedOutput.productName) {
        case ProductName.Facade:
          url = this.router.serializeUrl(this.router.createUrlTree(['/situ-facade'], { queryParams: { id: renderedOutput.id } }));
          break;
        case ProductName.DayDreamFacade:
          url = this.router.serializeUrl(this.router.createUrlTree(['/situ-daydream', renderedOutput.id]));
          break;
        default:
          url = this.router.serializeUrl(this.router.createUrlTree(['/situ-view'], { queryParams: { id: renderedOutput.id } }));
          break;
      }
    
      if (newWindow) {
        window.open(url, '_blank');
      } else {
        this.router.navigateByUrl(url);
      }
    }
    
    this.selectedRenderedOutput?.emit(renderedOutput);
  }

  getFacadeImage(artifactId: string) {
    return `${this.configService.config.situViewWebApiBaseUrl}/home/GetImage?artifactId=${artifactId}&fileName=facade.jpg&width=512`;
  }
  
  getPanoImage(artifactId: string) {
    return `${this.configService.config.situViewWebApiBaseUrl}/home/GetThumb?artifactId=${artifactId}`;
  }

  loadRenderedOutputs() {
    this.loading = true;
    let request = new ListRenderedOutputsRequest();
    request.companyId = this.selectedCompany?.id;
    request.pageNumber = this.viewControls.pageIndex + 1;
    request.pageSize = this.viewControls.pageSize;
    if (this.product !== undefined) request.productName = this.product;
    request.status = RenderedOutputStatus.Complete;
    if (this.isProductionOnly) request.isProduction = true;
    if (this.style) request.styleConfigTemplateId = this.style;
    if (this.config) request.homeConfigId = this.config;
    if (this.home) request.homeId = this.home;
    if (this.release) request.releaseId = this.release;
    if (this.styleTemplateName) request.styleTemplateName = this.styleTemplateName;
    if (this.masterFile) request.masterFile = this.masterFile;
    if (this.homeName) request.homeName = this.homeName;
    if (this.filter) request.containsWords = [this.filter];
    if (request.productName === ProductName.Panorama) request.expandProducts = true;

    this.situViewClient.listRenderedOutputs(request)
      .then((response: ListRenderedOutputsResponse) => {
        if (response.success && response?.renderedOutputs) {
          this.dataSource.data = response.renderedOutputs;
          this.viewControls.totalItems = response.totalItems ?? 0;
          this.loading = false;
          this.updateParams();
        } else {
          this.showMessage("Sorry! There was an issue at our end retrieving your renders. Please try again.");
        }
      })
      .catch((x: any) => {
        this.showMessage("Sorry! There was an issue at our end retrieving your renders. Please try again.");
      })
      .finally(() => {
        this.loading = false;
      });
  }

  loadSituConnectRenders() {
    this.loading = true;
    let request = new ListSituConnectSummariesRequest();
    request.companyId = this.selectedCompany?.id;
    request.pageNumber = this.viewControls.pageIndex + 1;
    request.pageSize = this.viewControls.pageSize;
    request.recipeStatus = RecipeStatus.Complete;
    if (this.filter) request.searchTerm = this.filter;

    this.situAnalyticsClient.listSituConnectSummaries(request)
      .then((response: ListSituConnectSummariesResponse) => {
        if (response.success && response?.items) {
          this.situConnectDataSource = response.items;
          this.viewControls.totalItems = response.totalItems ?? 0;
          this.loading = false;
        } else {
          this.showMessage("Sorry! There was an issue at our end retrieving your SituConnect renders. Please try again.");
        }
      })
      .catch((x: any) => {
        this.showMessage("Sorry! There was an issue at our end retrieving your SituConnect renders. Please try again.");
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private updateParams() {
    // A dialog-hosted browser must not rewrite the host page's URL — its filters are transient.
    if (this.embedded) {
      return;
    }

    const queryParams: { [key: string]: any } = {};

    if (this.filter) {
      queryParams.filter = this.filter;
    }
    if (this.isProductionOnly) {
      queryParams.liveOnly = "true";
    }
    if (this.viewControls.pageSize) {
      queryParams.pageSize = this.viewControls.pageSize;
    }
    if (this.style) {
      queryParams.style = this.style;
    }
    if (this.config) {
      queryParams.config = this.config;
    }
    if (this.home) {
      queryParams[this.HomeParamName] = this.home;
    }
    if (this.release) {
      queryParams[this.ReleaseParamName] = this.release;
    }
    if (this.styleTemplateName) {
      queryParams[this.StyleNameParamName] = this.styleTemplateName;
    }
    if (this.masterFile) {
      queryParams[this.MasterFileParamName] = this.masterFile;
    }
    if (this.homeName) {
      queryParams[this.HomeNameParamName] = this.homeName;
    }

    const urlTree = this.router.createUrlTree([], {
      queryParamsHandling: "merge",
      preserveFragment: true,
      queryParams: queryParams
    });

    if (!this.filter) {
      delete urlTree.queryParams[this.FilterParamName];
    }
    if (!this.isProductionOnly) {
      delete urlTree.queryParams[this.LiveOnlyParamName];
    }
    if (this.viewControls.pageSize === this.DefaultPageSize) {
      delete urlTree.queryParams[this.PageSizeParamName];
    }
    if (!this.style) {
      delete urlTree.queryParams[this.StyleParamName];
    }
    if (!this.config) {
      delete urlTree.queryParams[this.ConfigParamName];
    }
    if (!this.home) {
      delete urlTree.queryParams[this.HomeParamName];
    }
    if (!this.release) {
      delete urlTree.queryParams[this.ReleaseParamName];
    }
    if (!this.styleTemplateName) {
      delete urlTree.queryParams[this.StyleNameParamName];
    }
    if (!this.masterFile) {
      delete urlTree.queryParams[this.MasterFileParamName];
    }
    if (!this.homeName) {
      delete urlTree.queryParams[this.HomeNameParamName];
    }

    this.router.navigateByUrl(urlTree);
  }

  // SituConnect helper methods
  getConnectType(connectType: string | undefined) {
    // connectType is already the type, no need to parse it
    return connectType;
  }

  getConnectIconType(connectType: string | undefined): string {
    switch (connectType) {
      case 'draftsitulinkpanoramas':
      case 'situlinkpanoramas':
      case 'hdsitulinkpanoramas':
        return 'panorama';
      case 'facade':
      case 'hdfacade':
        return 'photo';
      default:
        return 'panorama';
    }
  }

  getConnectIconColor(connectType: string | undefined): string {
    switch (connectType) {
      case 'draftsitulinkpanoramas':
        return 'rgba(106, 76, 147, 1)'; // purple
      case 'situlinkpanoramas':
        return 'rgba(25, 130, 196, 1)'; // blue
      case 'hdsitulinkpanoramas':
        return 'rgba(20, 193, 245, 1)'; // light blue
      case 'facade':
      case 'hdfacade':
        return 'rgba(255, 89, 94, 1)'; // red
      default:
        return 'rgba(0, 0, 0, 1)';
    }
  }

  getConnectTypeTooltip(connectType: string | undefined): string {
    switch (connectType) {
      case 'draftsitulinkpanoramas':
        return 'situVIEW';
      case 'situlinkpanoramas':
        return 'situVIEW HD';
      case 'hdsitulinkpanoramas':
        return 'situVIEW HD+';
      case 'facade':
        return 'situFACADE';
      case 'hdfacade':
        return 'situFACADE HD';
      default:
        return 'SituConnect';
    }
  }

  getSituConnectImage(artifactId: string | undefined, connectType: string | undefined) {
    const isFacade = connectType === 'facade' || connectType === 'hdfacade';
    if (isFacade) {
      return `${this.configService.config.situViewWebApiBaseUrl}/home/GetImage?artifactId=${artifactId}&fileName=facade.jpg&width=512`;
    }
    return `${this.configService.config.situViewWebApiBaseUrl}/home/GetThumb?artifactId=${artifactId}`;
  }

  onSituConnectSelected(situConnect: SituConnectSummary, newWindow: boolean = false) {
    if (this.allowOpenSituViewOrFacade && situConnect.artifactId) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/situ-connect'], {
          queryParams: {
            id: situConnect.artifactId,
            type: situConnect.connectType
          }
        })
      );

      if (newWindow) {
        window.open(url, '_blank');
      } else {
        this.router.navigateByUrl(url);
      }
    }

    // Emit as a pseudo-RenderedOutputSummary for compatibility with theme editor
    const pseudoRenderedOutput: any = {
      artifactId: situConnect.artifactId,
      id: situConnect.artifactId,
      homeConfig: {
        home: situConnect.customerName || 'SituConnect',
        options: [],
        masterFile: situConnect.customerReference
      },
      productName: ProductName.Connect,
      dateCreated: situConnect.dateCompleted || situConnect.dateAdded,
      isProduction: false,
      callerReferenceKey: situConnect.connectType
    };

    this.selectedRenderedOutput?.emit(pseudoRenderedOutput);
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}